import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Dumbbell, Brain, Check, X, Sun, Moon, Sparkles, MoveRight
} from "lucide-react";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";

const WeeklyCalendar = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [weekData, setWeekData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWeekData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/week`);
      setWeekData(response.data.week);
    } catch (error) {
      devError("Failed to fetch week data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeekData();
  }, [fetchWeekData]);

  const getCompletionColor = (completed, total) => {
    if (total === 0) return "bg-slate-100";
    const percentage = (completed / total) * 100;
    if (percentage === 100) return "bg-green-100 border-green-300";
    if (percentage >= 50) return "bg-amber-50 border-amber-200";
    return "bg-sky-50 border-sky-200";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="weekly-calendar-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />
      
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('common.dashboard')}
            </Button>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t('calendar.title')}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500 mb-2">
              {t('calendar.trainingSchedule')}
            </p>
            <h1 className="text-3xl sm:text-4xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
              {t('calendar.title')}
            </h1>
          </div>

          <div className="grid grid-cols-7 gap-3">
            {weekData.map((day, idx) => (
              <div
                key={day.date}
                className={`
                  glass-card p-4 min-h-[280px] transition-all
                  ${day.is_today ? 'ring-2 ring-sky-400 shadow-lg' : ''}
                  ${day.is_past && !day.is_today ? 'opacity-70' : ''}
                `}
                data-testid={`day-${day.date}`}
              >
                <div className="text-center mb-4">
                  <p className="text-xs text-slate-500">{day.day_name.slice(0, 3)}</p>
                  <p className={`text-lg font-medium ${day.is_today ? 'text-sky-500' : 'text-sky-800'}`}>
                    {new Date(day.date).getDate()}
                  </p>
                  {day.is_today && (
                    <span className="text-xs bg-sky-400 text-white px-2 py-0.5 rounded-full">{t('common.today')}</span>
                  )}
                </div>

                <div className="flex items-center justify-center gap-1 mb-3">
                  {day.questionnaire_completed ? (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <Check className="w-3 h-3" />
                      <span>{t('calendar.checkedIn')}</span>
                    </div>
                  ) : day.is_past ? (
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <X className="w-3 h-3" />
                      <span>{t('calendar.missed')}</span>
                    </div>
                  ) : null}
                </div>

                {day.mood_score && (
                  <div className="flex justify-center gap-2 mb-3 text-xs">
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                      {day.mood_score}
                    </span>
                    <span className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded-full">
                      {day.energy_level}
                    </span>
                  </div>
                )}

                {day.has_plan && (
                  <div className="space-y-2">
                    <div className={`p-2 rounded-lg border ${getCompletionColor(day.physical_completed, day.physical_total)}`}>
                      <div className="flex items-center gap-2">
                        <Dumbbell className="w-4 h-4 text-cyan-500" strokeWidth={1.5} />
                        <span className="text-xs text-slate-600">{t('training.summary.physical')}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-700 mt-1">
                        {day.physical_completed}/{day.physical_total}
                      </p>
                    </div>
                    
                    <div className={`p-2 rounded-lg border ${getCompletionColor(day.cognitive_completed, day.cognitive_total)}`}>
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-indigo-500" strokeWidth={1.5} />
                        <span className="text-xs text-slate-600">{t('training.summary.cognitive')}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-700 mt-1">
                        {day.cognitive_completed}/{day.cognitive_total}
                      </p>
                    </div>
                  </div>
                )}

                {!day.has_plan && !day.is_past && (
                  <div className="text-center text-xs text-slate-400 mt-4">
                    {t('calendar.noPlanYet')}
                  </div>
                )}

                {day.is_today && !day.questionnaire_completed && (
                  <Button
                    size="sm"
                    className="w-full mt-3 glow-button text-xs"
                    onClick={() => navigate('/questionnaire')}
                  >
                    {t('dashboard.startCheckin')}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 glass-card p-4">
            <p className="text-sm text-slate-600 mb-3">{t('calendar.legend')}</p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                <span className="text-xs text-slate-600">{t('calendar.completed')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-50 border border-amber-200"></div>
                <span className="text-xs text-slate-600">{t('calendar.inProgress')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-sky-50 border border-sky-200"></div>
                <span className="text-xs text-slate-600">{t('calendar.pending')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded ring-2 ring-sky-400"></div>
                <span className="text-xs text-slate-600">{t('common.today')}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WeeklyCalendar;
